import { useEffect, useState, useMemo } from 'react';
import { salaryDeductionApi, siteApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { buildCycles } from '../../constants/deductionCycles';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DEDUCTION_TYPE_LABELS = {
  MATERIAL_REISSUE: 'Material Re-issue',
  DURABLE_NO_RETURN: 'Durable No Return',
  MAX_QUANTITY_EXCEEDED: 'Max Quantity Exceeded',
};

const DeductionTypeBadge = ({ type }) => (
  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
    type === 'MATERIAL_REISSUE'
      ? 'bg-indigo-100 text-indigo-700'
      : type === 'MAX_QUANTITY_EXCEEDED'
      ? 'bg-rose-100 text-rose-700'
      : 'bg-orange-100 text-orange-700'
  }`}>
    {DEDUCTION_TYPE_LABELS[type] || type}
  </span>
);

// ── Site Summary Card ─────────────────────────────────────────────────────────
const SiteCard = ({ site, total, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`text-left w-full rounded-xl border px-4 py-3 transition-all duration-150 ${
      active
        ? 'border-indigo-500 bg-indigo-50 shadow-sm'
        : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm'
    }`}
  >
    <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${active ? 'text-indigo-500' : 'text-gray-400'}`}>
      {site}
    </p>
    <p className={`text-lg font-bold leading-tight ${active ? 'text-indigo-800' : 'text-gray-800'}`}>
      {fmt(total)}
    </p>
    <p className={`text-xs mt-0.5 ${active ? 'text-indigo-500' : 'text-gray-400'}`}>
      {count} deduction{count !== 1 ? 's' : ''}
    </p>
  </button>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function SalaryDeductions() {
  const { user, isSiteRestricted: isStorekeeper } = useAuth();
  const cycles = buildCycles(6);

  const [selectedCycle, setSelectedCycle] = useState(cycles[0]?.cycleStart || '');
  const [activeSiteId,  setActiveSiteId]  = useState('');   // '' = All Sites
  const [deductions,    setDeductions]    = useState([]);
  const [sites,         setSites]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');

  // Load site list (non-storekeepers only)
  useEffect(() => {
    if (!isStorekeeper)
      siteApi.list().then(r => setSites(r.data.data || [])).catch(() => {});
  }, [isStorekeeper]);

  // Load deductions whenever the cycle changes — always the FULL cycle dataset
  // (no siteId sent), so the site breakdown below always has every site to
  // switch between. Site filtering happens client-side over this same set;
  // narrowing the fetch itself would make sites other than the selected one
  // vanish from the data entirely, leaving no way to switch back.
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (selectedCycle) params.cycleStart = selectedCycle;
        // Storekeepers are scoped server-side to their own site regardless.

        const res = await salaryDeductionApi.list(params);
        setDeductions(res.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load salary deductions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedCycle, isStorekeeper]);

  // ── Derive site-wise totals from the FULL (unfiltered) deductions list ────
  // Always computed over every site so the card grid — including "All Sites"
  // and every other site — stays clickable no matter which one is active.
  const siteMap = useMemo(() => {
    const map = {}; // siteId → { name, total, count }
    deductions.forEach(d => {
      const site = d.workerKit?.inventory?.site || d.stationeryIssuance?.site;
      if (!site) return;
      if (!map[site.id]) map[site.id] = { id: site.id, name: site.name, total: 0, count: 0 };
      map[site.id].total += parseFloat(d.amount || 0);
      map[site.id].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [deductions]);

  // ── What the total banner / table actually show — filtered client-side ───
  const visibleDeductions = useMemo(() => {
    if (isStorekeeper || !activeSiteId) return deductions;
    return deductions.filter(d => {
      const site = d.workerKit?.inventory?.site || d.stationeryIssuance?.site;
      return String(site?.id) === activeSiteId;
    });
  }, [deductions, activeSiteId, isStorekeeper]);

  const grandTotal = visibleDeductions.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
  // Always the true "every site" figure — independent of the current filter,
  // so the "All Sites" card doesn't shrink to whatever site happens to be active.
  const allSitesTotal = deductions.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
  const cycleLabel = cycles.find(c => c.cycleStart === selectedCycle)?.label || '';
  const activeSiteName = activeSiteId ? siteMap.find(s => String(s.id) === activeSiteId)?.name : null;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Salary Deductions"
        subtitle="Material re-issue and durable non-return deductions — site-wise view"
      />

      {error && (
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</p>
      )}

      {/* ── Cycle selector ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label text-xs mb-1">Deduction Cycle</label>
          <select
            className="input w-72"
            value={selectedCycle}
            onChange={e => { setSelectedCycle(e.target.value); setActiveSiteId(''); }}
          >
            {cycles.map(c => (
              <option key={c.cycleStart} value={c.cycleStart}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <>
          {/* ── Grand total banner ────────────────────────────────────────── */}
          {visibleDeductions.length > 0 && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500 mb-0.5">
                  Total Deductions — {isStorekeeper ? 'Your Site' : (activeSiteName || 'All Sites')}
                </p>
                <p className="text-2xl font-extrabold text-indigo-900">{fmt(grandTotal)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-indigo-600">{visibleDeductions.length} record{visibleDeductions.length !== 1 ? 's' : ''}</p>
                <p className="text-xs text-indigo-400 mt-0.5">{cycleLabel}</p>
              </div>
            </div>
          )}

          {/* ── Site summary cards ────────────────────────────────────────── */}
          {!isStorekeeper && siteMap.length > 1 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Site Breakdown</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {/* "All Sites" card — always the true all-sites figure, regardless of filter */}
                <SiteCard
                  site="All Sites"
                  total={allSitesTotal}
                  count={deductions.length}
                  active={activeSiteId === ''}
                  onClick={() => setActiveSiteId('')}
                />
                {siteMap.map(s => (
                  <SiteCard
                    key={s.id}
                    site={s.name}
                    total={s.total}
                    count={s.count}
                    active={activeSiteId === String(s.id)}
                    onClick={() => setActiveSiteId(activeSiteId === String(s.id) ? '' : String(s.id))}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Single-site summary (storekeeper or filtered) ─────────────── */}
          {(isStorekeeper || activeSiteId) && siteMap.length === 1 && (
            <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2H4a1 1 0 110-2V4zm3 1h2v2H7V5zm4 0h2v2h-2V5zM7 9h2v2H7V9zm4 0h2v2h-2V9z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Site</p>
                <p className="font-semibold text-gray-800">{siteMap[0].name}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Total</p>
                <p className="text-lg font-bold text-red-600">{fmt(siteMap[0].total)}</p>
              </div>
            </div>
          )}

          {/* ── Deductions table ──────────────────────────────────────────── */}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {!isStorekeeper && <th>Site</th>}
                  <th>Worker</th>
                  <th>Employee ID</th>
                  <th>Type</th>
                  <th>Material / Item</th>
                  <th>Amount (₹)</th>
                  <th>Reason</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {visibleDeductions.length === 0 ? (
                  <tr>
                    <td colSpan={isStorekeeper ? 7 : 8} className="text-center py-10 text-gray-400">
                      No deductions found for this cycle
                    </td>
                  </tr>
                ) : (
                  visibleDeductions.map(d => {
                    const site         = d.workerKit?.inventory?.site || d.stationeryIssuance?.site;
                    const materialName = d.workerKit?.kit?.name || d.stationeryIssuance?.item?.name || '—';
                    return (
                      <tr key={d.id}>
                        {!isStorekeeper && (
                          <td>
                            <span className="text-xs font-medium bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                              {site?.name || '—'}
                            </span>
                          </td>
                        )}
                        <td className="font-medium">{d.worker?.name}</td>
                        <td className="text-gray-500 font-mono text-sm">{d.worker?.employeeId}</td>
                        <td><DeductionTypeBadge type={d.deductionType} /></td>
                        <td className="text-gray-700">{materialName}</td>
                        <td className="font-semibold text-red-600">{fmt(d.amount)}</td>
                        <td className="text-gray-500 text-sm max-w-xs whitespace-normal break-words">{d.reason}</td>
                        <td className="text-gray-400 text-xs">{new Date(d.createdAt).toLocaleDateString('en-GB')}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {visibleDeductions.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={isStorekeeper ? 4 : 5} className="text-right text-sm text-gray-600">
                      Total
                    </td>
                    <td className="text-red-700 font-bold">{fmt(grandTotal)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}
